<script setup lang="ts">
import {ref, watch} from "vue";
import {StaffingTarget} from "@/core/interfaces/TeamPlanning";
import {StaffingTargetInput} from "@/core/http/resources/TeamPlanningResource";
import {DAY_SHORT_LABELS, DISPLAY_DAYS, toMinutes, toTime} from "@/team/planning";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Dialog, DialogContent, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import {Plus, X} from "@lucide/vue";

const STEP_MINUTES = 30;

const {open, targets} = defineProps<{
    open: boolean;
    targets: StaffingTarget[];
    saving: boolean;
}>();

const emits = defineEmits<{
    (event: 'update:open', open: boolean): void;
    (event: 'save', targets: StaffingTargetInput[]): void;
}>();

/** Editable copy: slots grouped by day, Monday first, Sunday excluded. */
const DAYS = DISPLAY_DAYS.filter(day => day !== 0);

const draft = ref<StaffingTargetInput[]>([]);

watch(() => [open, targets], () => {
    if (open) {
        draft.value = targets.map(target => ({
            day_of_week: target.day_of_week,
            starts_at: target.starts_at,
            ends_at: target.ends_at,
            headcount: target.headcount,
        }));
    }
}, {immediate: true});

const slotsOf = (day: number) => draft.value.filter(slot => slot.day_of_week === day);

const addSlot = (day: number) => {
    draft.value.push({day_of_week: day, starts_at: '09:00', ends_at: '19:00', headcount: 2});
};

const removeSlot = (slot: StaffingTargetInput) => {
    draft.value = draft.value.filter(item => item !== slot);
};

/** Empties the draft only: nothing is lost until « Enregistrer », and « Annuler » still backs out. */
const clearAll = () => {
    draft.value = [];
};

const nudgeTime =(slot: StaffingTargetInput, bound: 'starts_at' | 'ends_at', direction: 1 | -1) => {
    const next = toMinutes(slot[bound]) + direction * STEP_MINUTES;

    if (bound === 'starts_at') {
        slot.starts_at = toTime(Math.min(Math.max(next, 0), toMinutes(slot.ends_at) - STEP_MINUTES));
    } else {
        slot.ends_at = toTime(Math.max(Math.min(next, 23 * 60 + 30), toMinutes(slot.starts_at) + STEP_MINUTES));
    }
};

const nudgeHeadcount = (slot: StaffingTargetInput, direction: 1 | -1) => {
    slot.headcount = Math.min(Math.max(slot.headcount + direction, 0), 50);
};
</script>

<template>
  <Dialog :open="open" @update:open="value => emits('update:open', value)">
    <DialogContent class="max-w-xl">
      <DialogHeader>
        <DialogTitle>Effectif cible</DialogTitle>
        <p class="text-xs text-gray-400">
          Nombre de personnes souhaité par tranche horaire — comparé aux présences planifiées.
        </p>
      </DialogHeader>

      <div class="flex max-h-[55vh] flex-col gap-3 overflow-y-auto pr-1">
        <div v-for="day in DAYS" :key="day" class="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5">
          <div class="mb-1.5 flex items-center">
            <span class="flex-1 text-[11px] font-bold text-gray-600">{{ DAY_SHORT_LABELS[day] }}</span>
            <button
              class="flex h-6 items-center gap-1 rounded-md border border-gray-200 bg-white px-2 text-[10px] font-semibold text-primary hover:border-primary/40"
              @click="addSlot(day)"
            >
              <Plus class="size-3" />
              Tranche
            </button>
          </div>
          <div v-if="!slotsOf(day).length" class="text-[10px] text-gray-300">
            Aucune cible ce jour.
          </div>
          <div v-for="(slot, index) in slotsOf(day)" :key="index" class="mb-1 flex flex-wrap items-center gap-2">
            <div v-for="bound in (['starts_at', 'ends_at'] as const)" :key="bound" class="flex items-center gap-1">
              <button class="size-6 rounded border border-gray-200 bg-white text-xs text-gray-500" @click="nudgeTime(slot, bound, -1)">−</button>
              <span class="w-11 text-center text-[11px] font-bold">{{ slot[bound] }}</span>
              <button class="size-6 rounded border border-gray-200 bg-white text-xs text-gray-500" @click="nudgeTime(slot, bound, 1)">+</button>
            </div>
            <div class="flex items-center gap-1">
              <button class="size-6 rounded border border-gray-200 bg-white text-xs text-gray-500" @click="nudgeHeadcount(slot, -1)">−</button>
              <span class="w-14 text-center text-[11px] font-bold">{{ slot.headcount }} pers.</span>
              <button class="size-6 rounded border border-gray-200 bg-white text-xs text-gray-500" @click="nudgeHeadcount(slot, 1)">+</button>
            </div>
            <button
              :title="'Supprimer la tranche'"
              class="flex size-6 items-center justify-center rounded border border-gray-200 bg-white text-red-400 hover:text-red-600"
              @click="removeSlot(slot)"
            >
              <X class="size-3" />
            </button>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between">
        <BaseButton :disabled="saving || !draft.length" @click="clearAll">
          Tout effacer
        </BaseButton>
        <div class="flex gap-2">
          <BaseButton @click="emits('update:open', false)">
            Annuler
          </BaseButton>
          <BaseButton primary :loading="saving" @click="emits('save', draft)">
            Enregistrer
          </BaseButton>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
